import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileSignature, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { EsignTracker } from '../components/esign/EsignTracker';
import { esignAPI, errMsg, esignLabel, saveBlob } from '../lib/esignAPI';
import { EsignHolds } from '../components/esign/EsignHolds';

export default function EngagementSign() {
  const [data, setData] = useState(undefined);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(null);

  useEffect(() => {
    esignAPI.my().then(setData).catch((e) => { toast.error(errMsg(e)); setData({ letter: null }); });
  }, []);
  const lt = data?.letter;
  useEffect(() => {
    if (!lt?.id) return undefined;
    let url;
    esignAPI.draftBlob(lt.id).then((b) => { url = URL.createObjectURL(b); setPdfUrl(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [lt?.id]);

  const proceed = async () => {
    setBusy(true);
    try {
      const r = await esignAPI.start(lt.id, { acknowledged: true, consent_text_version: data.consent_version });
      if (r.url) { window.location.assign(r.url); return; }
      window.location.assign(`/onboarding/signing-complete?letter=${lt.id}`);
    } catch (e) {
      if (e?.response?.status === 503) setUnavailable(errMsg(e)); else toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (data === undefined) return <div className="p-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!lt) {
    return (
      <div className="mx-auto max-w-3xl p-6" data-testid="esign-none">
        <Card><CardContent className="p-8 text-center text-muted-foreground">Your engagement letter isn&apos;t ready yet. We&apos;ll notify you as soon as it is.</CardContent></Card>
      </div>
    );
  }
  const done = ['signed', 'onboarding_completed'].includes(lt.esign_status);
  const iSigned = lt.my_status === 'SIGNED';
  const blocked = ['declined', 'expired', 'cancelled'].includes(lt.esign_status);
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6" data-testid="esign-page">
      <EsignTracker status={lt.esign_status} consented={lt.consented} />
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{lt.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="esign-meta">
          <span className="font-medium text-foreground" data-testid="esign-status-label">{esignLabel(lt.esign_status, lt.consented)}</span> · Document {lt.ps_document_id} · generated {lt.generated_at ? new Date(lt.generated_at).toLocaleDateString('en-AU') : '—'}
        </p>
      </div>
      {(unavailable || !lt.provider_available) && !done && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="esign-unavailable">
          <ShieldAlert className="mt-0.5 h-4 w-4" />{unavailable || lt.unavailable_message}
        </div>
      )}
      {done && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-emerald-900" data-testid="esign-done">
          <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5" />Signed {lt.completed_at ? new Date(lt.completed_at).toLocaleString('en-AU') : ''}</span>
          <Button size="sm" onClick={async () => saveBlob(await esignAPI.signedBlob(lt.id), `${lt.ps_document_id}-signed.pdf`)} data-testid="esign-download-signed"><Download className="mr-2 h-4 w-4" />Signed copy</Button>
        </div>
      )}
      <EsignHolds holds={lt.holds} testId="esign-holds" />
      {blocked && <p className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="esign-blocked">
        {{ declined: 'You declined to sign this engagement letter. Your accountant has been notified and will contact you about next steps.',
          expired: 'The signing period for this engagement letter has ended. Your accountant will send you a new letter to sign.',
          cancelled: 'This engagement letter was withdrawn. Your accountant will send a replacement.' }[lt.esign_status]}</p>}
      {lt.signers.length > 1 && (
        <Card><CardContent className="space-y-1 pt-5 text-sm" data-testid="esign-signers">
          {lt.signers.map((s) => <div key={s.signing_order} className="flex justify-between"><span>{s.signing_order}. {s.name}{s.is_me ? ' (you)' : ''}</span><span>{s.status === 'SIGNED' ? 'Signed' : 'Waiting'}</span></div>)}
        </CardContent></Card>
      )}
      {pdfUrl ? <iframe title="Engagement letter preview" src={pdfUrl} className="h-[70vh] w-full rounded-md border" data-testid="esign-preview" />
        : <div className="flex h-64 items-center justify-center rounded-md border text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      {!done && !blocked && !iSigned && (
        <Card><CardContent className="space-y-4 pt-6">
          {lt.allow_draft_download && pdfUrl && <a href={pdfUrl} download={`${lt.ps_document_id}-draft.pdf`} className="inline-flex items-center text-sm text-primary underline" data-testid="esign-download-draft"><Download className="mr-1 h-4 w-4" />Download draft</a>}
          <label className="flex items-start gap-3 text-sm" htmlFor="esign-ack">
            <Checkbox id="esign-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="esign-ack" />
            <span>I have read and understood this engagement letter. {data.consent_text}</span>
          </label>
          <p className="text-xs text-muted-foreground">Ticking this box is not a signature. You&apos;ll sign on the secure signing page next, then return here.</p>
          <Button disabled={!ack || busy} onClick={proceed} data-testid="esign-proceed">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}Agree &amp; proceed to sign</Button>
        </CardContent></Card>
      )}
      {iSigned && !done && <p className="rounded-md border p-4 text-sm" data-testid="esign-waiting-others">You&apos;ve signed. We&apos;re waiting for the other signatories.</p>}
      {lt.clause_consents?.length > 0 && (
        <Card><CardContent className="space-y-1 pt-6 text-sm" data-testid="esign-clause-consents">
          <p className="font-medium">Your acknowledgements</p>
          {lt.clause_consents.map((c, i) => <p key={i}>{c.signer_name}: AI use {c.ai_acknowledged ? 'acknowledged' : 'not ticked'}{c.offshore_authorised === null || c.offshore_authorised === undefined ? '' : ` · offshore services ${c.offshore_authorised ? 'authorised' : 'declined'}`}</p>)}
          <p className="text-xs text-muted-foreground">The full consent record is in your Documents.</p>
        </CardContent></Card>)}
    </div>
  );
}
